import React, { useEffect, ReactNode, useRef } from 'react';
import { FocusableButton } from '@/components/common/FocusableButton';
import { FocusContext, setFocus, getCurrentFocusKey } from '@noriginmedia/norigin-spatial-navigation';
import { dialogFocusLock } from '@/utils/dialogFocusLock';
import styles from './ConfirmDialog.module.scss';

export interface ConfirmDialogProps {
  /** 主标题 */
  title: string;
  /** 副标题 */
  subtitle?: string;
  /** 自定义内容体 Slot */
  contentSlot?: ReactNode;
  /** 确认按钮文本 */
  confirmText?: string;
  /** 取消按钮文本 */
  cancelText?: string;
  /** 确认回调 */
  onConfirm: () => void;
  /** 取消回调 */
  onCancel: () => void;
  /** 是否显示 */
  visible: boolean;
  /** 是否使用横向按钮布局 */
  useHorizontalButtons?: boolean;
}

/**
 * 通用确认对话框组件
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  title,
  subtitle,
  contentSlot,
  confirmText = '确认',
  cancelText = '取消',
  onConfirm,
  onCancel,
  visible,
  useHorizontalButtons = false,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null);

  // 当对话框显示时，锁定背景滚动，自动聚焦到确认按钮
  useEffect(() => {
    if (visible) {
      const focusKey = 'confirm-dialog-confirm';

      // 1. 锁定 body 滚动，防止背景抖动
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';

      // 2. 焦点管理逻辑
      dialogFocusLock.lock();
      window.dispatchEvent(new CustomEvent('confirm-dialog-show'));
      setFocus(focusKey);

      const handleFocusChange = () => {
        const currentFocus = getCurrentFocusKey();
        if (currentFocus && !currentFocus.startsWith('confirm-dialog-')) {
          setFocus(focusKey);
        }
      };

      document.addEventListener('focusin', handleFocusChange);

      return () => {
        document.body.style.overflow = originalStyle;
        document.removeEventListener('focusin', handleFocusChange);
        dialogFocusLock.unlock();
      };
    }
  }, [visible]);

  if (!visible) {
    return null;
  }

  return (
    <FocusContext.Provider value="confirm-dialog-context">
      <div
        className={styles.overlay}
        ref={overlayRef}
        onWheel={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <div className={styles.dialog} onWheel={(e) => e.stopPropagation()}>
          {/* 标题区域 */}
          <div className={styles.content}>
            <h2 className={styles.title}>{title}</h2>
            {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
          </div>

          {/* 自定义内容体 Slot */}
          {contentSlot && <div className={styles.customContent}>{contentSlot}</div>}

          {/* 按钮区域 */}
          <div className={useHorizontalButtons ? styles.buttonsHorizontal : styles.buttons}>
            <FocusableButton
              focusKey="confirm-dialog-confirm"
              onClick={onConfirm}
              onMouseEnter={() => setFocus('confirm-dialog-confirm')}
              className={useHorizontalButtons ? styles.buttonHorizontal : styles.button}
              focusedClassName={useHorizontalButtons ? styles.buttonHorizontalFocused : styles.buttonFocused}
              onBackPress={() => {
                // 🔑 确认按钮上按返回键：触发取消回调（与取消按钮行为一致）
                onCancel();
                return true; // 返回 true 阻止事件传播，避免触发全局返回键处理
              }}
            >
              {confirmText}
            </FocusableButton>
            <FocusableButton
              focusKey="confirm-dialog-cancel"
              onClick={onCancel}
              onMouseEnter={() => setFocus('confirm-dialog-cancel')}
              className={useHorizontalButtons ? styles.buttonHorizontal : styles.button}
              focusedClassName={useHorizontalButtons ? styles.buttonHorizontalFocused : styles.buttonFocused}
              onBackPress={() => {
                // 🔑 取消按钮上按返回键：触发取消回调
                onCancel();
                return true; // 返回 true 阻止事件传播，避免触发全局返回键处理
              }}
            >
              {cancelText}
            </FocusableButton>
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  );
};
